import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ gridColumns: string }>()((theme, { gridColumns }) => ({
    container: {
        borderRadius: 8,
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
    },
    headerTitle: {
        fontWeight: 600,
    },
    columnHeader: {
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
    },
    columnHeaderCell: {
        fontWeight: 600,
        fontSize: '0.6875rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gap: theme.spacing(1),
        alignItems: 'center',
        padding: theme.spacing(0.75, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-of-type': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    cellText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    centeredState: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2),
    },
}));

export type ExpandedPanelClasses = ReturnType<typeof useStyles>['classes'];

interface ExpandedPanelProps {
    title: string;
    columnHeaders: string[];
    gridColumns: string;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
    loadingMessage: string;
    errorMessage: string;
    emptyMessage: string;
    /** Row content rendered when data is available. Receives shared classes. */
    children: (classes: ExpandedPanelClasses) => ReactNode;
    /** Optional footer (e.g. show-more button). */
    footer?: ReactNode;
}

export const ExpandedPanel = ({
    title,
    columnHeaders,
    gridColumns,
    isLoading,
    isError,
    isEmpty,
    loadingMessage,
    errorMessage,
    emptyMessage,
    children,
    footer,
}: ExpandedPanelProps) => {
    const { classes } = useStyles({ gridColumns });

    if (isLoading) {
        return (
            <Paper variant="outlined" className={classes.container}>
                <Box className={classes.centeredState}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                        {loadingMessage}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (isError) {
        return (
            <Paper variant="outlined" className={classes.container}>
                <Box className={classes.centeredState}>
                    <Typography variant="body2" color="error">
                        {errorMessage}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (isEmpty) {
        return (
            <Paper variant="outlined" className={classes.container}>
                <Box className={classes.centeredState}>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {emptyMessage}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" className={classes.container}>
            <Box className={classes.header}>
                <Typography variant="subtitle2" className={classes.headerTitle}>
                    {title}
                </Typography>
            </Box>

            <Box className={classes.columnHeader}>
                {columnHeaders.map((label) => (
                    <Typography key={label} className={classes.columnHeaderCell}>
                        {label}
                    </Typography>
                ))}
            </Box>

            {children(classes)}

            {footer}
        </Paper>
    );
};
